import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { Urls } from "../../../utils/constant";
import { useApi } from "../apiCall/useApi";
import {
    hidePageLoader,
    showPageLoader,
} from "../../reducers/Common/PageLoader/PageLoader.reducer";

export default function useDoctorSlotBlock() {
    const dispatch = useDispatch();
    const { apiPost } = useApi();

    const [loading, setLoading] = useState(false);
    const [slotBlockList, setSlotBlockList] = useState([]);
    const [paginationInfo, setPaginationInfo] = useState({ total_page: 0, total_count: 0 });

    const [doctorId, setDoctorId] = useState("");
    const [areaId, setAreaId] = useState("");
    const [blockDate, setBlockDate] = useState(null);

    const [doctorList, setDoctorList] = useState([]);
    const [areaList, setAreaList] = useState([]);

    // Fetch doctors for combo box
    const fetchDoctorComboList = useCallback(async () => {
        try {
            const { data } = await apiPost({ url: Urls.getDoctorCombo(), data: {} });
            setDoctorList(data?.data || []);
        } catch (err) {
            console.log(err);
        }
    }, [apiPost]);

    // Fetch areas/locations for combo box
    const fetchAreaComboList = useCallback(async () => {
        try {
            const { data } = await apiPost({ url: Urls.getAreaCombo(), data: {} });
            setAreaList(Array.isArray(data) ? data : data?.data || []);
        } catch (err) {
            console.log(err);
        }
    }, [apiPost]);

    const findDoctorFromLabel = useCallback(
        (value) => {
            const selected = value ? doctorList.find((item) => item.label === value)?.id : null;
            setDoctorId(selected);
            return selected;
        },
        [doctorList]
    );

    const findLocationFromLabel = useCallback(
        (value) => {
            const selected = value ? areaList.find((item) => item.label === value)?.id : null;
            setAreaId(selected);
            return selected;
        },
        [areaList]
    );

    const fetchSlotBlockList = useCallback(
        async ({ current_page = 1, row_count }) => {
            if (loading) return;
            setLoading(true);
            try {
                dispatch(showPageLoader());
                const { data } = await apiPost({
                    url: Urls.doctorSlotBlockGet ? Urls.doctorSlotBlockGet() : "/doctor_slot_block/get",
                    data: {
                        doctor_id: doctorId,
                        area_id: areaId,
                        block_date: blockDate,
                        page_size: row_count,
                        current_page: current_page,
                    },
                });

                let outputData = Array.isArray(data) ? data : (data?.data ? (Array.isArray(data.data) ? data.data : [data.data]) : [data]);

                // Handle backend returning { success: [...], current_page: ... } vs raw array
                if (data && data.success !== undefined) {
                    // Incase it's an object with lists
                    // Your default list API structure is mostly straight array mappings, but pagination sits somewhere
                    setSlotBlockList(data.success === 1 || data.data ? data.data : outputData);
                    setPaginationInfo({
                        total_page: data.total_page || 0,
                        total_count: data.total_count || 0,
                    });
                } else if (Array.isArray(outputData) && outputData.length > 0 && outputData[0] !== undefined) {
                    setSlotBlockList(outputData);
                    // Check if pagination info exists in the first object if the backend flattens them
                    setPaginationInfo({
                        total_page: outputData[0].total_page || 1,
                        total_count: outputData[0].total_count || outputData.length,
                    });
                } else {
                    setSlotBlockList([]);
                    setPaginationInfo({ total_page: 0, total_count: 0 });
                }

                setLoading(false);
                dispatch(hidePageLoader());
            } catch (err) {
                dispatch(hidePageLoader());
                console.log(err);
                setLoading(false);
                setSlotBlockList([]);
            }
        }, [dispatch, apiPost, doctorId, areaId, blockDate]);

    const deleteDoctorSlotBlock = useCallback(
        async ({ id }) => {
            try {
                dispatch(showPageLoader());
                const { data } = await apiPost({
                    url: Urls.doctorSlotBlockDelete(),
                    data: { id, user_id: "" },
                });
                dispatch(hidePageLoader());
                return data;
            } catch (err) {
                dispatch(hidePageLoader());
                console.log(err);
                return null;
            }
        },
        [dispatch, apiPost]
    );

    return {
        loading,
        slotBlockList,
        paginationInfo,
        doctorList,
        areaList,
        doctorId,
        areaId,
        blockDate,
        setBlockDate,
        fetchDoctorComboList,
        fetchAreaComboList,
        findDoctorFromLabel,
        findLocationFromLabel,
        fetchSlotBlockList,
        deleteDoctorSlotBlock,
    };
}
